import { useCallback, useContext, useEffect, useState } from 'react';

import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import {
  AtomMap,
  itemsInStack,
  latLngToUrlValue,
  MarkerLayer,
  SearchPin,
  urlValueToLatLng,
  useDefaultMapLocation,
} from '@atom/map';
import {
  InventoryMapFilters,
  MapIdleEvent,
  MapSearch,
  ViewState,
} from '@atom/types/map';

import InventoryPortalContext from '../InventoryPortalContext';

import { useInventoryMapData } from './useInventoryMapData';

const SEARCH_ZOOM = 15;

type InventoryMapProps = {
  filters: InventoryMapFilters;
};

const InventoryMap = ({ filters }: InventoryMapProps) => {
  const { activeCategory, stackAssets, setStackAssets } = useContext(
    InventoryPortalContext,
  );
  const defaultLocation = useDefaultMapLocation();

  const [
    { center, zoom, searchPoint, searchTerm, id, stack },
    { updateSearchParams },
  ] = useNavigation({
    center: SearchParamType.STRING,
    zoom: SearchParamType.NUMBER,
    searchPoint: SearchParamType.STRING,
    searchTerm: SearchParamType.STRING,
    id: SearchParamType.STRING,
    stack: SearchParamType.STRING,
  });

  const urlCenter = center ? urlValueToLatLng(center) : null;

  const initialViewState: ViewState = {
    center: urlCenter ?? defaultLocation.center,
    zoom: zoom || defaultLocation.zoom,
    searchPoint: searchPoint ? urlValueToLatLng(searchPoint) : undefined,
    searchTerm: searchTerm ?? undefined,
  };

  const [viewport, setViewport] = useState<MapIdleEvent | null>(null);
  const coordinates = viewport?.coordinates ?? null;
  const zoomLevel = viewport?.zoom ?? null;

  const params = {
    coordinates,
    zoomLevel,
    ...(activeCategory.id !== 'inventory' && {
      categoryIds: activeCategory.id,
    }),
    ...filters,
  };

  const [mapData, loading] = useInventoryMapData(params);

  useEffect(() => {
    if (!stack) {
      if (stackAssets.length > 0) {
        setStackAssets([]);
      }
      return;
    }

    const members = itemsInStack(mapData.assets ?? [], stack);
    const memberIds = members.map(asset => asset.id).join(',');
    const currentIds = stackAssets.map(asset => asset.id).join(',');

    if (members.length > 0) {
      if (memberIds !== currentIds) {
        setStackAssets(members);
      }
      return;
    }

    const belongsToStack =
      itemsInStack(stackAssets, stack).length === stackAssets.length;

    if (!belongsToStack) {
      setStackAssets([]);
    }
  }, [stack, mapData.assets, stackAssets, setStackAssets]);

  const handleIdle = (view: MapIdleEvent) => {
    setViewport(view);
    updateSearchParams({
      center: latLngToUrlValue(view.center),
      zoom: view.zoom,
    });
  };

  const handleSearchChange = (search: MapSearch) =>
    search.searchPoint
      ? updateSearchParams({
          searchPoint: latLngToUrlValue(search.searchPoint),
          searchTerm: search.searchTerm ?? null,
          center: latLngToUrlValue(search.searchPoint),
          zoom: SEARCH_ZOOM,
        })
      : updateSearchParams({ searchPoint: null, searchTerm: null });

  const handleMarkerClick = useCallback(
    (markerId: string) => {
      updateSearchParams({ id: markerId, stack: null });
    },
    [updateSearchParams],
  );

  const handleAssetGroupClick = useCallback(
    (stackKey: string) => {
      const groupAssets = itemsInStack(mapData.assets ?? [], stackKey);
      setStackAssets(groupAssets);
      updateSearchParams({ stack: stackKey, id: null });
    },
    [mapData.assets, setStackAssets, updateSearchParams],
  );

  const searchPinPosition = searchPoint ? urlValueToLatLng(searchPoint) : null;

  return (
    <AtomMap
      initialViewState={initialViewState}
      onIdle={handleIdle}
      onSearchChange={handleSearchChange}
      loading={loading}
    >
      <MarkerLayer
        data={mapData}
        activeId={id ?? ''}
        activeStack={stack ?? ''}
        onAssetGroupClick={handleAssetGroupClick}
        onMarkerClick={handleMarkerClick}
      />
      <SearchPin position={searchPinPosition} />
    </AtomMap>
  );
};

export default InventoryMap;
